import { Injectable, Injector, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, defer, finalize, share, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Property, RoomType, RatePlan, BookingRow, RateCalendarEntry,
  CreatePropertyRequest, UpdatePropertyRequest,
  CreateRoomTypeRequest, UpdateRoomTypeRequest, CreateRatePlanRequest,
  BulkRateRequest, BulkRestrictionRequest, BulkInventoryRequest, CmLockRequest,
  BulkUpdateResponse, PresignedUrlResponse, BookingUpdateRequest, RegisterOfflineOrderRequest, AmenityGroup,
  RoomGuests, UpdateStayRequest,
  HotelGeoSummary, ImportPropertyRequest,
  RatePlanCatalogEntry, RoomTypeCatalogEntry, MeResponse,
  CreatePropertyGroupRequest, InviteMemberRequest, PropertyGroupMemberResponse,
  PropertyGroupResponse, UpdatePropertyGroupRequest, UpdateMyGroupRequest, VoucherSendRequest,
  PlatformCurrency,
  RatePlanCatalogEntryRequest, RoomTypeCatalogEntryRequest,
  PropertyTranslationsResponse, PropertyTranslationUpsertRequest, SetDefaultLanguageRequest, LanguageContent,
  RoomTypeTranslationsResponse, RoomTypeTranslationUpsertRequest, RoomTypeLanguageContent,
  LanguageResponse, CreateLanguageRequest, UpdateLanguageStatusRequest,
  AiPromptResponse, AiPromptPreviewRequest, AiPromptPreviewResponse, AiPromptSaveRequest,
  AdminPropertySearchResult,
  AgencyClient, AgencyClientRow, AgencyClientUpsertRequest, ClientOrderRow, ClientPayment,
  ClientPaymentRequest, ClientsDashboard, PagedResponse, StayQuote,
  PricedRangeQuery, PricedRangeResponse,
  BookingCmActions, PublishReadiness,
} from '../models/extranet.model';
import { OrderListParams, OrdersSummary, toOrderQueryParams } from '../models/orders.model';
import { ViewAsService } from './view-as.service';

@Injectable({ providedIn: 'root' })
export class ExtranetService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl;
  /** ViewAsService is read lazily (its pinned group, in getMe): resolving it here would make every
   *  spec that builds this service provide the view-as dependencies too. */
  private readonly injector = inject(Injector);

  // Access — resolves the caller's real, BE-derived role (see hotel-manager.guard.ts).
  // If the user has picked a role via the dev-only "View as" switcher (IdentityService.switchRole,
  // persisted in sessionStorage), it's forwarded as ?activeRole=; the BE only honours it within the
  // account's own availableRoles AND only when the switcher is enabled server-side (dev/local), so
  // sending it in production is a harmless no-op. A query param (not a header) keeps this free of
  // any CORS-preflight allow-listing. Every guard calls getMe() fresh, so they all see the pick.
  //
  // In-flight de-duplication (Feature 4123): the role guard and the area guard of one navigation
  // both ask for the identity at the same moment, so a caller subscribing while an identical
  // request (same activeRole pick, same view-as group) is still out shares that request instead of
  // sending its own. The view-as group is the one the view-as interceptor sends as the header
  // (`ViewAsService.group`): a GET /me started before `enter()` / `exit()` answers for the other
  // identity, so the reload after the switch never joins it. The entry lives only while the request
  // is in flight — it is dropped when it answers, fails, completes empty or loses its last
  // subscriber — so it is never a cache: the next navigation asks the server again.
  getMe(): Observable<MeResponse> {
    return defer(() => {
      const active = ExtranetService.getActiveRolePreference();
      const viewAsGroupId = this.injector.get(ViewAsService).group()?.id ?? '';
      const key = `${active ?? ''}|${viewAsGroupId}`;
      const pending = this.meInFlight.get(key);
      if (pending) return pending;
      const url = `${this.base}/extranet/access/me`;
      // Dropped as soon as the answer arrives (before its subscribers see it, so one of them asking
      // again starts a new request instead of joining a finished one), and on any other ending.
      const forget = () => { if (this.meInFlight.get(key) === request) this.meInFlight.delete(key); };
      const request: Observable<MeResponse> = this.http
        .get<MeResponse>(active ? `${url}?activeRole=${encodeURIComponent(active)}` : url)
        .pipe(tap(forget), finalize(forget), share());
      this.meInFlight.set(key, request);
      return request;
    });
  }

  /** The GET /me requests in flight, by `<activeRole pick>|<view-as group id>` ('' = none). */
  private readonly meInFlight = new Map<string, Observable<MeResponse>>();

  /** sessionStorage key for the dev-only role-switcher pick — per-tab, cleared on tab close. */
  static readonly ACTIVE_ROLE_KEY = 'ext.activeRole';

  static getActiveRolePreference(): string | null {
    try { return sessionStorage.getItem(ExtranetService.ACTIVE_ROLE_KEY); } catch { return null; }
  }

  // Platform-admin: create a property group + its first member in one call
  createPropertyGroup(req: CreatePropertyGroupRequest): Observable<PropertyGroupMemberResponse> {
    return this.http.post<PropertyGroupMemberResponse>(`${this.base}/extranet/access/groups`, req);
  }

  // An active member invites a peer into their own group
  invitePeer(req: InviteMemberRequest): Observable<PropertyGroupMemberResponse> {
    return this.http.post<PropertyGroupMemberResponse>(`${this.base}/extranet/access/groups/members`, req);
  }

  // Platform-admin: every property group on the platform (Admin > Property groups list)
  listPropertyGroups(): Observable<PropertyGroupResponse[]> {
    return this.http.get<PropertyGroupResponse[]>(`${this.base}/extranet/access/groups`);
  }

  getPropertyGroup(id: string): Observable<PropertyGroupResponse> {
    return this.http.get<PropertyGroupResponse>(`${this.base}/extranet/access/groups/${id}`);
  }

  updatePropertyGroup(id: string, req: UpdatePropertyGroupRequest): Observable<PropertyGroupResponse> {
    return this.http.put<PropertyGroupResponse>(`${this.base}/extranet/access/groups/${id}`, req);
  }

  // Self-service — the caller's own property group (Settings > Property group tab). 404s for a
  // caller with no active group membership (e.g. a platform admin).
  getMyGroup(): Observable<PropertyGroupResponse> {
    return this.http.get<PropertyGroupResponse>(`${this.base}/extranet/access/groups/mine`);
  }

  updateMyGroup(req: UpdateMyGroupRequest): Observable<PropertyGroupResponse> {
    return this.http.put<PropertyGroupResponse>(`${this.base}/extranet/access/groups/mine`, req);
  }

  /** Group logo (0055, revised -- BE-proxied instead of a presigned direct-to-bucket PUT: the
   *  original presigned-URL design, matching property photos, needed the storage bucket's own
   *  CORS policy to allow this origin, which turned out to not be configured in a real
   *  environment and is infrastructure this app doesn't control. The BE now takes the raw bytes,
   *  validates them, uploads them itself, and returns the updated group in one round trip -- no
   *  separate `updateMyGroup` call needed. */
  uploadGroupLogo(file: File): Observable<PropertyGroupResponse> {
    return this.http.post<PropertyGroupResponse>(
      `${this.base}/extranet/access/groups/mine/logo`, file,
      { headers: new HttpHeaders({ 'Content-Type': file.type }) });
  }

  /** The currencies a group, bank account, contract, expense or supplier may be in — the
   *  Extranet's static list (SAR, USD, EUR, EGP, AED, GBP; WI 3534), served by the Extranet BE.
   *  The Extranet never converts: prices stay in the currency they were entered in (the group's)
   *  and b2bhotels applies the exchange rate. It used to come from be-agency-service, which the
   *  Extranet has no relationship with (and whose APIM route refused the browser). */
  listPlatformCurrencies(): Observable<PlatformCurrency[]> {
    return this.http.get<PlatformCurrency[]>(`${this.base}/extranet/catalogue/currencies`);
  }

  listGroupMembers(id: string): Observable<PropertyGroupMemberResponse[]> {
    return this.http.get<PropertyGroupMemberResponse[]>(`${this.base}/extranet/access/groups/${id}/members`);
  }

  // Platform-admin: invite a member into an explicit group (Property groups list's "Invite a
  // member" row action) — invitePeer() above only ever resolves the caller's OWN group, which
  // doesn't work for an admin with no group of their own.
  inviteMemberIntoGroup(groupId: string, req: InviteMemberRequest): Observable<PropertyGroupMemberResponse> {
    return this.http.post<PropertyGroupMemberResponse>(`${this.base}/extranet/access/groups/${groupId}/members`, req);
  }

  // Properties
  listProperties(): Observable<Property[]> {
    return this.http.get<Property[]>(`${this.base}/extranet/properties`);
  }

  getProperty(id: string): Observable<Property> {
    return this.http.get<Property>(`${this.base}/extranet/properties/${id}`);
  }

  createProperty(req: CreatePropertyRequest): Observable<Property> {
    return this.http.post<Property>(`${this.base}/extranet/properties`, req);
  }

  /** "Search & import a property" wizard, step 1 — hotels near a Google-Places-resolved point. */
  searchNearbyHotelsForImport(lat: number, lng: number, radiusKm = 5): Observable<HotelGeoSummary[]> {
    return this.http.get<HotelGeoSummary[]>(`${this.base}/extranet/properties/import/search-nearby`, {
      params: { latitude: lat, longitude: lng, radius: radiusKm },
    });
  }

  /** "Search & import a property" wizard, final step — fetches Amadeus content and creates the Draft. */
  importProperty(req: ImportPropertyRequest): Observable<Property> {
    return this.http.post<Property>(`${this.base}/extranet/properties/import`, req);
  }

  updateProperty(id: string, req: UpdatePropertyRequest): Observable<Property> {
    return this.http.patch<Property>(`${this.base}/extranet/properties/${id}`, req);
  }

  publish(id: string): Observable<Property> {
    return this.http.post<Property>(`${this.base}/extranet/properties/${id}/publish`, {});
  }

  /** WI 3780: what the property still lacks before a publish is accepted (any status). */
  getPublishReadiness(id: string): Observable<PublishReadiness> {
    return this.http.get<PublishReadiness>(`${this.base}/extranet/properties/${id}/publish-readiness`);
  }

  depublish(id: string): Observable<Property> {
    return this.http.post<Property>(`${this.base}/extranet/properties/${id}/depublish`, {});
  }

  presignedUrl(propertyId: string): Observable<PresignedUrlResponse> {
    return this.http.post<PresignedUrlResponse>(
      `${this.base}/extranet/properties/${propertyId}/photos/presigned-url`, {});
  }

  /** The browser's PUT straight to Spaces, with the headers the URL was signed with: the content
   *  type and the public-read ACL that makes the photo readable (WI 3703, 3814). An older BE sends
   *  no uploadHeaders: then the content type alone, as before. */
  private putToSpaces(p: PresignedUrlResponse, file: File, contentType: string): Observable<string> {
    return this.http.put(p.uploadUrl, file, {
      headers: new HttpHeaders(p.uploadHeaders ?? { 'Content-Type': contentType }),
      responseType: 'text',
    });
  }

  uploadCoverPhoto(propertyId: string, file: File): Observable<string> {
    return this.presignedUrl(propertyId).pipe(
      switchMap(p =>
        this.putToSpaces(p, file, file.type || 'image/webp').pipe(
          switchMap(() => this.updateProperty(propertyId, { coverPhotoKey: p.storageKey })),
          switchMap(() => [p.publicUrl]),
        )
      )
    );
  }

  uploadPhoto(propertyId: string, file: File): Observable<string> {
    return this.presignedUrl(propertyId).pipe(
      switchMap(p =>
        this.putToSpaces(p, file, 'image/webp').pipe(
          switchMap(() => [p.publicUrl]),
        )
      )
    );
  }

  // Room types
  listRoomTypes(propertyId: string): Observable<RoomType[]> {
    return this.http.get<RoomType[]>(`${this.base}/extranet/properties/${propertyId}/room-types`);
  }

  createRoomType(propertyId: string, req: CreateRoomTypeRequest): Observable<RoomType> {
    return this.http.post<RoomType>(`${this.base}/extranet/properties/${propertyId}/room-types`, req);
  }

  updateRoomType(roomTypeId: string, req: UpdateRoomTypeRequest): Observable<RoomType> {
    return this.http.patch<RoomType>(`${this.base}/extranet/room-types/${roomTypeId}`, req);
  }

  // Reference catalogues
  getRoomAmenityCatalogue(): Observable<AmenityGroup[]> {
    return this.http.get<AmenityGroup[]>(`${this.base}/extranet/catalogue/room-amenities`);
  }

  /** Platform rate-plan catalog (pick-list of shared code+name). */
  getRatePlanCatalogue(): Observable<RatePlanCatalogEntry[]> {
    return this.http.get<RatePlanCatalogEntry[]>(`${this.base}/extranet/catalogue/rate-plans`);
  }

  /** Platform room-type catalog (physical type x class). */
  getRoomTypeCatalogue(): Observable<RoomTypeCatalogEntry[]> {
    return this.http.get<RoomTypeCatalogEntry[]>(`${this.base}/extranet/catalogue/room-types`);
  }

  // Admin > Rate plan catalog (usageCount populated, unlike the public getRatePlanCatalogue() above)
  adminListRatePlanCatalogue(): Observable<RatePlanCatalogEntry[]> {
    return this.http.get<RatePlanCatalogEntry[]>(`${this.base}/extranet/admin/catalogue/rate-plans`);
  }

  createRatePlanCatalogEntry(req: RatePlanCatalogEntryRequest): Observable<RatePlanCatalogEntry> {
    return this.http.post<RatePlanCatalogEntry>(`${this.base}/extranet/admin/catalogue/rate-plans`, req);
  }

  updateRatePlanCatalogEntry(id: string, req: RatePlanCatalogEntryRequest): Observable<RatePlanCatalogEntry> {
    return this.http.put<RatePlanCatalogEntry>(`${this.base}/extranet/admin/catalogue/rate-plans/${id}`, req);
  }

  deleteRatePlanCatalogEntry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/extranet/admin/catalogue/rate-plans/${id}`);
  }

  // Admin > Room type catalog (usageCount populated, unlike the public getRoomTypeCatalogue() above)
  adminListRoomTypeCatalogue(): Observable<RoomTypeCatalogEntry[]> {
    return this.http.get<RoomTypeCatalogEntry[]>(`${this.base}/extranet/admin/catalogue/room-types`);
  }

  createRoomTypeCatalogEntry(req: RoomTypeCatalogEntryRequest): Observable<RoomTypeCatalogEntry> {
    return this.http.post<RoomTypeCatalogEntry>(`${this.base}/extranet/admin/catalogue/room-types`, req);
  }

  updateRoomTypeCatalogEntry(id: string, req: RoomTypeCatalogEntryRequest): Observable<RoomTypeCatalogEntry> {
    return this.http.put<RoomTypeCatalogEntry>(`${this.base}/extranet/admin/catalogue/room-types/${id}`, req);
  }

  deleteRoomTypeCatalogEntry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/extranet/admin/catalogue/room-types/${id}`);
  }

  deleteRoomType(propertyId: string, roomTypeId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/extranet/properties/${propertyId}/room-types/${roomTypeId}`);
  }

  // Rate plans
  listRatePlans(roomTypeId: string): Observable<RatePlan[]> {
    return this.http.get<RatePlan[]>(`${this.base}/extranet/room-types/${roomTypeId}/rate-plans`);
  }

  createRatePlan(roomTypeId: string, req: CreateRatePlanRequest): Observable<RatePlan> {
    return this.http.post<RatePlan>(`${this.base}/extranet/room-types/${roomTypeId}/rate-plans`, req);
  }

  updateRatePlan(roomTypeId: string, ratePlanId: string, req: CreateRatePlanRequest): Observable<RatePlan> {
    return this.http.put<RatePlan>(`${this.base}/extranet/room-types/${roomTypeId}/rate-plans/${ratePlanId}`, req);
  }

  deleteRatePlan(roomTypeId: string, ratePlanId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/extranet/room-types/${roomTypeId}/rate-plans/${ratePlanId}`);
  }

  /** WI 3673 contract M3.3: how many of a base plan's nights from today (or in the bulk edit's
   *  `from`/`to`/`daysOfWeek` scope) are priced and hold prices by guests. A read, allowed in a
   *  read-only view-as. Only the parameters given reach the wire; `daysOfWeek` is repeated. */
  getPricedRange(ratePlanId: string, scope: PricedRangeQuery = {}): Observable<PricedRangeResponse> {
    const params: Record<string, string | string[]> = {};
    if (scope.from) params['from'] = scope.from;
    if (scope.to) params['to'] = scope.to;
    if (scope.daysOfWeek?.length) params['daysOfWeek'] = scope.daysOfWeek;
    return this.http.get<PricedRangeResponse>(`${this.base}/extranet/rate-plans/${ratePlanId}/priced-range`, { params });
  }

  // Calendar
  listRates(ratePlanId: string, from: string, to: string): Observable<RateCalendarEntry[]> {
    return this.http.get<RateCalendarEntry[]>(
      `${this.base}/extranet/rate-plans/${ratePlanId}/rates`,
      { params: { from, to } }
    );
  }

  bulkPutRates(ratePlanId: string, req: BulkRateRequest): Observable<BulkUpdateResponse> {
    return this.http.put<BulkUpdateResponse>(`${this.base}/extranet/rate-plans/${ratePlanId}/rates`, req);
  }

  /** Clears a derived plan's manual price overrides over a date range (revert to Auto).
   *  daysOfWeek omitted/undefined clears every day in range (unchanged single-cell behavior). */
  clearRateOverride(ratePlanId: string, from: string, to: string, daysOfWeek?: string[]): Observable<BulkUpdateResponse> {
    return this.http.delete<BulkUpdateResponse>(
      `${this.base}/extranet/rate-plans/${ratePlanId}/rates`,
      { params: daysOfWeek?.length ? { from, to, daysOfWeek } : { from, to } }
    );
  }

  bulkPutRestrictions(ratePlanId: string, req: BulkRestrictionRequest): Observable<BulkUpdateResponse> {
    return this.http.put<BulkUpdateResponse>(`${this.base}/extranet/rate-plans/${ratePlanId}/restrictions`, req);
  }

  /** WI 3667 L3.2: pause / resume eZee updates on the existing rate cells of a BASE plan (a derived
   *  plan answers 400 RATE_PLAN_DERIVED_READONLY; a property not connected to eZee 409
   *  CM_NOT_CONNECTED). `affectedDates` = the cells whose pause state changed. */
  putRatePlanCmLock(ratePlanId: string, req: CmLockRequest): Observable<BulkUpdateResponse> {
    return this.http.put<BulkUpdateResponse>(`${this.base}/extranet/rate-plans/${ratePlanId}/cm-lock`, req);
  }

  /** WI 3667 L3.2: the same on a room type's availability cells only, never its rate plans. */
  putRoomTypeCmLock(roomTypeId: string, req: CmLockRequest): Observable<BulkUpdateResponse> {
    return this.http.put<BulkUpdateResponse>(`${this.base}/extranet/room-types/${roomTypeId}/cm-lock`, req);
  }

  listBookings(propertyId: string): Observable<BookingRow[]> {
    return this.http.get<BookingRow[]>(`${this.base}/extranet/properties/${propertyId}/bookings`);
  }

  updateBooking(reservationId: string, req: BookingUpdateRequest): Observable<BookingRow> {
    return this.http.patch<BookingRow>(`${this.base}/extranet/bookings/${reservationId}`, req);
  }

  registerOfflineOrder(propertyId: string, req: RegisterOfflineOrderRequest): Observable<BookingRow> {
    return this.http.post<BookingRow>(`${this.base}/extranet/properties/${propertyId}/offline-bookings`, req);
  }

  getGuests(reservationId: string): Observable<RoomGuests[]> {
    return this.http.get<RoomGuests[]>(`${this.base}/extranet/bookings/${reservationId}/guests`);
  }

  updateGuests(reservationId: string, rooms: RoomGuests[]): Observable<RoomGuests[]> {
    return this.http.put<RoomGuests[]>(`${this.base}/extranet/bookings/${reservationId}/guests`, { rooms });
  }

  updateStay(reservationId: string, req: UpdateStayRequest): Observable<BookingRow> {
    return this.http.patch<BookingRow>(`${this.base}/extranet/bookings/${reservationId}/stay`, req);
  }

  /** The rendered voucher PDF (0054) — same bytes the email attachment uses. Display via a
   *  sanitized `blob:` object URL (see VoucherModalComponent), never write it to disk here.
   *  `lang` (en/it/fr/es/de/ar) picks the document's own language, independent of the app's UI
   *  language; omitted falls back BE-side to the issuing group's own default. */
  getVoucherPdf(reservationId: string, lang?: string): Observable<Blob> {
    return this.http.get(`${this.base}/extranet/bookings/${reservationId}/voucher`, {
      responseType: 'blob',
      params: lang ? { lang } : {},
    });
  }

  /** Fire-and-track voucher email send — 202 Accepted, delivery outcome lands async on the booking
   *  row (`voucherDeliveryStatus`/`voucherSentAt`), re-fetch the booking to observe it. */
  sendVoucher(reservationId: string, req: VoucherSendRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/extranet/bookings/${reservationId}/voucher/send`, req);
  }

  bulkPutInventory(roomTypeId: string, req: BulkInventoryRequest): Observable<BulkUpdateResponse> {
    return this.http.put<BulkUpdateResponse>(`${this.base}/extranet/room-types/${roomTypeId}/inventory`, req);
  }

  // Multi-language content — Property
  getPropertyTranslations(propertyId: string): Observable<PropertyTranslationsResponse> {
    return this.http.get<PropertyTranslationsResponse>(`${this.base}/extranet/properties/${propertyId}/translations`);
  }

  upsertPropertyTranslation(propertyId: string, langCode: string, req: PropertyTranslationUpsertRequest): Observable<LanguageContent> {
    return this.http.put<LanguageContent>(`${this.base}/extranet/properties/${propertyId}/translations/${langCode}`, req);
  }

  setPropertyDefaultLanguage(propertyId: string, req: SetDefaultLanguageRequest): Observable<PropertyTranslationsResponse> {
    return this.http.put<PropertyTranslationsResponse>(`${this.base}/extranet/properties/${propertyId}/default-language`, req);
  }

  // Multi-language content — Room Type (description only, inherits the property's default language)
  getRoomTypeTranslations(roomTypeId: string): Observable<RoomTypeTranslationsResponse> {
    return this.http.get<RoomTypeTranslationsResponse>(`${this.base}/extranet/room-types/${roomTypeId}/translations`);
  }

  upsertRoomTypeTranslation(roomTypeId: string, langCode: string, req: RoomTypeTranslationUpsertRequest): Observable<RoomTypeLanguageContent> {
    return this.http.put<RoomTypeLanguageContent>(`${this.base}/extranet/room-types/${roomTypeId}/translations/${langCode}`, req);
  }

  // Admin > Languages
  adminListLanguages(): Observable<LanguageResponse[]> {
    return this.http.get<LanguageResponse[]>(`${this.base}/extranet/admin/languages`);
  }

  // Any authenticated caller — LIVE languages only, no platform-admin gate (unlike
  // adminListLanguages() above). Backs the "Default content language" pickers on the property
  // group wizard/edit-panel/self-service Settings tab, where the caller is often not an admin.
  listLiveLanguages(): Observable<LanguageResponse[]> {
    return this.http.get<LanguageResponse[]>(`${this.base}/extranet/languages`);
  }

  adminCreateLanguage(req: CreateLanguageRequest): Observable<LanguageResponse> {
    return this.http.post<LanguageResponse>(`${this.base}/extranet/admin/languages`, req);
  }

  adminUpdateLanguageStatus(code: string, req: UpdateLanguageStatusRequest): Observable<LanguageResponse> {
    return this.http.patch<LanguageResponse>(`${this.base}/extranet/admin/languages/${code}`, req);
  }

  // Admin > AI Prompts
  adminListAiPrompts(): Observable<AiPromptResponse[]> {
    return this.http.get<AiPromptResponse[]>(`${this.base}/extranet/admin/ai-prompts`);
  }

  adminPreviewAiPrompt(key: string, req: AiPromptPreviewRequest): Observable<AiPromptPreviewResponse> {
    return this.http.post<AiPromptPreviewResponse>(`${this.base}/extranet/admin/ai-prompts/${key}/preview`, req);
  }

  adminSaveAiPrompt(key: string, req: AiPromptSaveRequest): Observable<AiPromptResponse> {
    return this.http.put<AiPromptResponse>(`${this.base}/extranet/admin/ai-prompts/${key}`, req);
  }

  adminSearchProperties(query: string): Observable<AdminPropertySearchResult[]> {
    return this.http.get<AdminPropertySearchResult[]>(`${this.base}/extranet/admin/properties/search`, {
      params: { q: query },
    });
  }

  // Agency > Clients — every endpoint is AGENCY_MANAGER-only and scoped server-side to the
  // caller's property group. All money comes back in the group currency (echoed on the DTOs).

  /** status omitted = every client (ACTIVE and ARCHIVED); pass 'ACTIVE' to hide archived. */
  listClients(search?: string, status?: 'ACTIVE' | 'ARCHIVED'): Observable<AgencyClientRow[]> {
    const params: Record<string, string> = {};
    if (search) params['search'] = search;
    if (status) params['status'] = status;
    return this.http.get<AgencyClientRow[]>(`${this.base}/extranet/clients`, { params });
  }

  getClient(clientId: string): Observable<AgencyClientRow> {
    return this.http.get<AgencyClientRow>(`${this.base}/extranet/clients/${clientId}`);
  }

  createClient(req: AgencyClientUpsertRequest): Observable<AgencyClient> {
    return this.http.post<AgencyClient>(`${this.base}/extranet/clients`, req);
  }

  updateClient(clientId: string, req: AgencyClientUpsertRequest): Observable<AgencyClient> {
    return this.http.patch<AgencyClient>(`${this.base}/extranet/clients/${clientId}`, req);
  }

  listClientOrders(clientId: string, page: number, size: number): Observable<PagedResponse<ClientOrderRow>> {
    return this.http.get<PagedResponse<ClientOrderRow>>(`${this.base}/extranet/clients/${clientId}/orders`, {
      params: { page, size },
    });
  }

  listClientPayments(clientId: string, page: number, size: number): Observable<PagedResponse<ClientPayment>> {
    return this.http.get<PagedResponse<ClientPayment>>(`${this.base}/extranet/clients/${clientId}/payments`, {
      params: { page, size },
    });
  }

  registerClientPayment(clientId: string, req: ClientPaymentRequest): Observable<ClientPayment> {
    return this.http.post<ClientPayment>(`${this.base}/extranet/clients/${clientId}/payments`, req);
  }

  /** Reversal, not deletion — the ledger is append-only; the BE sets deleted_at. */
  reverseClientPayment(clientId: string, paymentId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/extranet/clients/${clientId}/payments/${paymentId}`);
  }

  getClientsDashboard(): Observable<ClientsDashboard> {
    return this.http.get<ClientsDashboard>(`${this.base}/extranet/clients/dashboard`);
  }

  /** Stay total derived from the rate calendar (incl. manual overrides) — powers the offline
   *  order panel's quoted price. Never blocks on unpriced nights; see StayQuote. */
  quoteStay(roomTypeId: string, ratePlanId: string, checkIn: string, checkOut: string, rooms: number): Observable<StayQuote> {
    return this.http.get<StayQuote>(`${this.base}/extranet/rates/quote`, {
      params: { roomTypeId, ratePlanId, checkIn, checkOut, rooms },
    });
  }

  // Agency-wide orders (F0 BE, WI #3303) — AGENCY_MANAGER only, scoped to the caller's group.
  // The per-property `listBookings` stays the hotel manager's list and the booking detail's source
  // of the FULL BookingRow (the rows below do not carry hotelRef/voucher/option fields, see
  // ClientOrderRow). `page` is 1-based; `size` is capped at 100 by the BE.
  listOrders(params: OrderListParams): Observable<PagedResponse<ClientOrderRow>> {
    return this.http.get<PagedResponse<ClientOrderRow>>(`${this.base}/extranet/orders`, {
      params: toOrderQueryParams(params),
    });
  }

  /** Tiles over the same filtered set as `listOrders` (unpaged) — `page`/`size` are ignored. */
  getOrdersSummary(params: OrderListParams): Observable<OrdersSummary> {
    const { page: _page, size: _size, ...filter } = params;
    return this.http.get<OrdersSummary>(`${this.base}/extranet/orders/summary`, {
      params: toOrderQueryParams(filter),
    });
  }

  /** Option → booking (handoff §12): PENDING → CONFIRMED, `optionDate` kept on the record,
   *  `optionConfirmedAt` stamped, inventory untouched. 422 NOT_ON_OPTION when not PENDING. */
  confirmOption(reservationId: string): Observable<BookingRow> {
    return this.http.post<BookingRow>(`${this.base}/extranet/bookings/${reservationId}/confirm-option`, null);
  }

  /** Release an option: PENDING → CANCELLED through the cancel path, so the rooms go back to
   *  availability (AC-12.7). 422 NOT_ON_OPTION when not PENDING. */
  releaseOption(reservationId: string): Observable<BookingRow> {
    return this.http.post<BookingRow>(`${this.base}/extranet/bookings/${reservationId}/release-option`, null);
  }

  // eZee channel manager of one booking (WI 3669 contract B7.1 / B7.2). Called only for a booking
  // of a connected property or one with eZee history (B7.8): never for an agency's booking.

  /** What the booking sent to eZee: one row per action, newest first, plus the chip state. */
  getBookingCmActions(reservationId: string): Observable<BookingCmActions> {
    return this.http.get<BookingCmActions>(`${this.base}/extranet/bookings/${reservationId}/cm-actions`);
  }

  /** Queues one action again (`actionId`) or every retryable action of the booking (no id). The
   *  body is always sent — `{actionId}` or `{}` — because the BE answers 400 to a POST without one;
   *  the 200 is the read model after the change. 409 `CM_RETRY_NOT_ALLOWED` / `CM_NOT_CONNECTED`
   *  when nothing could be queued. */
  retryBookingCmActions(reservationId: string, actionId?: string | null): Observable<BookingCmActions> {
    return this.http.post<BookingCmActions>(
      `${this.base}/extranet/bookings/${reservationId}/cm-actions/retry`, actionId ? { actionId } : {});
  }
}
